/**
 * Cart-item delete — what the client posts to `DELETE /api/cart` to remove a
 * single row from `cart_items`. The route validates that `userId` (when
 * present) matches the authenticated session; guests pass `guestId` instead.
 *
 * Used today only by the "rollback cart additions made in this flow" path
 * in `removeListingFromCart` (`lib/cart-quick-checkout.ts`), invoked when
 * the buyer dismisses the quick-checkout sheet after having added items in
 * that same flow. Generalisable to any rollback / explicit remove path.
 *
 * @see app/api/cart/route.ts DELETE — handler
 * @see lib/cart-quick-checkout.ts removeListingFromCart — caller
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface DeleteCartItemRequest {
  /** `cart_items.id` to delete. Required. */
  id: string;
  /**
   * Authenticated user id. The route re-validates it against the session —
   * a mismatch returns 403. Mutually exclusive with `guestId` (XOR — exactly
   * one populated at the wire level; callers omit the absent side via
   * conditional spread or `?? undefined` rather than sending `null`).
   */
  userId?: string;
  /** Guest cart id when the buyer is not authenticated. */
  guestId?: string;
}

// Runtime parse — Zod schema (boundary). `pinned<DeleteCartItemRequest>()` is
// the compile-time schema↔interface pin; see `./pin.ts`.
export const DeleteCartItemRequestSchema = pinned<DeleteCartItemRequest>()(
  z.object({
    id: z.string().min(1),
    userId: z.string().min(1).optional(),
    guestId: z.string().min(1).optional(),
  }),
);
